import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { graphql, useFragment } from "react-relay";
import { logger } from "@/common/logger";
import { SlotContext, useOnceSlot } from "@/utils/suspense";
import { WorkspaceRendererFragment$key } from "./__generated__/WorkspaceRendererFragment.graphql";
import { useReadyWorkspaceRunner } from "@/utils/workspace";
import { invariant } from "@/utils/helpers";
import WorkspacePhaseProgress from "../WorkspacePhaseProgress";

interface FetchRenderOptions extends RequestInit {
  hideCode?: boolean | undefined;
}

interface Render {
  html: string;
  fonts: string[];
  styles: string[];
  images: string[];
}

async function fetchRender(
  href: string,
  { hideCode, ...init }: FetchRenderOptions = {},
): Promise<Render> {
  const url = new URL(href);
  url.searchParams.set("t", "json");
  if (hideCode) {
    url.searchParams.set("hide-code", "true");
  } else {
    url.searchParams.delete("hide-code");
  }
  const response = await fetch(url, init);
  if (response.status < 200 || response.status >= 300) {
    if (response.headers.get("content-type")?.startsWith("text/plain")) {
      const responseData = await response.text();
      throw new Error(responseData);
    }
    throw new Error(`Cannot load workspace: server status ${response.status}`);
  }
  const { html, fonts, styles, images }: Render = await response.json();
  // The runner builds absolute URLs from the Host header with its own (http)
  // scheme, which the browser blocks as mixed content when the page is https.
  // Rebase them onto the origin we fetched from.
  const update = (str: string) =>
    str.replaceAll(`http://${url.host}`, url.origin);
  return {
    html: update(html),
    fonts: fonts.map(update),
    styles: styles.map(update),
    images: images.map(update),
  };
}

async function fetchRender2(
  newHost: string,
  href: string,
  options: FetchRenderOptions = {},
) {
  const url = new URL(href);
  const gw = new URL(newHost);
  const origin = url.origin;
  url.protocol = gw.protocol;
  url.host = gw.host;
  url.port = gw.port;
  const update = (str: string) => str.replaceAll(gw.origin, origin);
  const { html, fonts, styles, images } = await fetchRender(
    url.toString(),
    options,
  );
  return {
    html: update(html),
    fonts: fonts.map(update),
    styles: styles.map(update),
    images: images.map(update),
  };
}

const Fragment = graphql`
  fragment WorkspaceRendererFragment on WorkspaceRunner {
    id
    phase
    url

    ...WorkspacePhaseProgressFragment
  }
`;

export type WorkspaceRendererInteraction =
  | {
      type: "click";
      objectId: string;
      value?: string | undefined;
      testid?: string | undefined;
    }
  | {
      type: "change";
      objectId: string;
      value: string;
    };

export interface WorkspaceRendererProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> {
  workspace: WorkspaceRendererFragment$key;
  hideCode?: boolean | undefined;
  onInteracted?:
    | ((interaction: WorkspaceRendererInteraction) => void)
    | undefined;
  /// The static render could not be loaded (e.g. the runner has no render
  /// cache for this notebook); nothing is shown in its place.
  onRenderError?: ((error: unknown) => void) | undefined;
}

export function WorkspaceRenderer(props: WorkspaceRendererProps) {
  if (import.meta.env.SSR) {
    return (
      <SlotContext>
        <ServerWorkspaceRenderer {...props} />
      </SlotContext>
    );
  } else {
    return <ClientWorkspaceRenderer {...props} />;
  }
}

function ServerWorkspaceRenderer({
  workspace: workspaceFragment,
  hideCode,
  onInteracted: _onInteracted,
  onRenderError: _onRenderError,
  ...props
}: WorkspaceRendererProps) {
  const {
    id: runnerId,
    phase,
    url,
    ...runner
  } = useFragment(Fragment, workspaceFragment);
  const isReady = phase === "READY";

  const render = useOnceSlot(
    useCallback(async () => {
      if (!isReady) {
        return;
      }
      const opts: FetchRenderOptions = { hideCode };
      if (import.meta.env.VITE_KUBIMO_HOST) {
        return await fetchRender2(import.meta.env.VITE_KUBIMO_HOST, url, opts);
      } else {
        return await fetchRender(url, opts);
      }
    }, [url, isReady, hideCode]),
  );

  if (!isReady) {
    return (
      <div className="lg:w-[66%] m-auto">
        <WorkspacePhaseProgress workspace={runner} />
      </div>
    );
  }

  if (!render) {
    return null;
  }

  const shadowLinks = render.styles
    .map((style) => `<link rel="stylesheet" crossorigin href="${style}"/>`)
    .join("\n");

  const scriptId = crypto.randomUUID();
  const documentLinks = render.fonts
    .filter((font) => font.endsWith(".css"))
    .map(
      (font) =>
        `<link rel="stylesheet" crossorigin href="${font}" data-workspace="${runnerId}"/>`,
    );

  return (
    <>
      <div
        key={runnerId}
        dangerouslySetInnerHTML={{
          /*
           * SAFETY: everything we receive has already been sanitized
           */
          __html: `<template shadowrootmode="open">${shadowLinks}${render.html}</template>`,
        }}
        suppressHydrationWarning
        {...props}
      />
      <script
        id={scriptId}
        dangerouslySetInnerHTML={{
          /*
           * SAFETY: this trick loads some assets during page initialization, but before react is hydrating the page.
           * we remove the script after assets have been appended, in order to avoid hydration issues.
           * moreover, there's no user input here.
           */
          __html: `document.head.innerHTML += ${JSON.stringify(documentLinks.join(""))};document.getElementById(${JSON.stringify(scriptId)}).remove();`,
        }}
        suppressHydrationWarning
      />
    </>
  );
}

function ClientWorkspaceRenderer({
  workspace: workspaceFragment,
  hideCode,
  onInteracted,
  onRenderError,
  ...divProps
}: WorkspaceRendererProps) {
  const runner = useFragment(Fragment, workspaceFragment);
  const isReady = useReadyWorkspaceRunner(runner);
  const innerRef = useRef<HTMLDivElement>(null);
  const onInteractedRef = useRef(onInteracted);
  onInteractedRef.current = onInteracted;
  const onRenderErrorRef = useRef(onRenderError);
  onRenderErrorRef.current = onRenderError;

  const { id: runnerId, url } = runner;
  useEffect(() => {
    const host = innerRef.current;
    if (!host || !isReady) return;

    /*
     * Makes the shadow root interactive:
     * 1. we keep a list of every interactive elements in the shadow root
     * 2. we hook a click listener up
     * 3. we eventually disconnect listeners on component cleanup
     */
    const onInteractElement = (event: Event) => {
      if (!onInteractedRef.current) return;
      event.preventDefault();
      event.stopPropagation();

      const target = event.currentTarget;
      invariant(target instanceof HTMLElement);

      const parent = target.closest("[data-object-id]");
      invariant(
        parent !== null &&
          parent instanceof HTMLElement &&
          !!parent.dataset.objectId,
      );

      const objectId = parent.dataset.objectId;

      if (event.type === "change" && target instanceof HTMLSelectElement) {
        onInteractedRef.current({
          type: "change",
          objectId,
          value: target.value,
        });
      } else if (event.type === "click") {
        onInteractedRef.current({
          type: "click",
          objectId,
          value: target instanceof HTMLButtonElement ? target.value : undefined,
          testid: target.dataset.testid,
        });
      } else {
        logger.error(
          `Cannot handle interaction "${event.type}" on "${target.nodeName}"`,
        );
      }
    };
    const interactiveElements = new Map<
      keyof HTMLElementEventMap,
      HTMLElement[]
    >();
    const setupInteractions = () => {
      if (!host.shadowRoot) return;

      interactiveElements.set("click", [
        ...host.shadowRoot.querySelectorAll("button"),
        ...host.shadowRoot.querySelectorAll("input"),
      ]);
      interactiveElements.set("change", [
        ...host.shadowRoot.querySelectorAll("select"),
      ]);

      for (const [event, elements] of interactiveElements.entries()) {
        for (const elm of elements) {
          elm.addEventListener(event, onInteractElement);
        }
      }
    };
    const stopInteractions = () => {
      for (const [event, elements] of interactiveElements.entries()) {
        for (const elm of elements) {
          elm.removeEventListener(event, onInteractElement);
        }
      }

      interactiveElements.clear();
    };

    /*
     * Loads the shadow root body which has been generated ahead-of-time
     * SAFETY: we assign directly `shadowRoot.innerHTML` because
     * everything we receive has already been sanitized
     */
    const loadShadowRoot = async (signal: AbortSignal) => {
      const render = await fetchRender(url, { signal, hideCode });
      const shadowRoot = host.attachShadow({ mode: "open" });
      await Promise.all([
        ...render.styles.map((style) =>
          loadLink(
            shadowRoot,
            { rel: "stylesheet", crossOrigin: "anonymous", href: style },
            signal,
          ),
        ),
        ...render.fonts
          .filter((font) => font.endsWith(".css"))
          .map((style) =>
            loadLink(
              document.head,
              {
                rel: "stylesheet",
                crossOrigin: "anonymous",
                href: style,
                dataset: { workspace: runnerId },
              },
              signal,
            ),
          ),
      ]);

      const tpl = document.createElement("template");
      tpl.innerHTML = render.html;
      shadowRoot.append(tpl.content.cloneNode(true));
    };

    const cleanDocumentHead = () => {
      for (const link of document.head.querySelectorAll(
        `[data-workspace="${runnerId}"]`,
      )) {
        link.remove();
      }
    };

    if (host.shadowRoot) {
      // Shadow root already mounted since the page has been rendered on the server
      setupInteractions();
      return () => {
        stopInteractions();
        if (import.meta.env.PROD) {
          cleanDocumentHead();
        } else {
          // NOTE: in development components are re-mounted and it breaks styling
          // because rendering on the server we loose info on the client and cannot
          // re-added links that were removed. It's just development mode so it's
          // okay if some resource are not cleaned up.
        }
      };
    } else {
      // Need to load the shadow root on the client

      const abort = new AbortController();

      void (async () => {
        await loadShadowRoot(abort.signal);
        setupInteractions();
      })().catch((error) => {
        logger.error(error);
        onRenderErrorRef.current?.(error);
      });

      return () => {
        abort.abort();
        stopInteractions();
        cleanDocumentHead();
      };
    }
  }, [hideCode, isReady, runnerId, url]);

  if (!isReady) {
    return (
      <div className="lg:w-[66%] m-auto">
        <WorkspacePhaseProgress workspace={runner} />
      </div>
    );
  }

  return (
    <div
      ref={innerRef}
      key={runner.id}
      dangerouslySetInnerHTML={{ __html: "" }}
      suppressHydrationWarning
      {...divProps}
    />
  );
}

function loadLink(
  target: Node,
  props: Partial<HTMLLinkElement> = {},
  signal?: AbortSignal | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const $link = document.createElement("link");
    for (const [key, value] of Object.entries(props)) {
      if (typeof value === "object") {
        // @ts-expect-error: let's just hope it works
        Object.assign($link[key], value);
      } else {
        // @ts-expect-error: let's just hope it works
        $link[key] = value;
      }
    }
    $link.addEventListener("load", () => resolve(), { once: true });
    $link.addEventListener("error", (event) => reject(event.error), {
      once: true,
    });
    signal?.addEventListener("abort", () => {
      $link.remove();
      reject("Aborted");
    });
    target.appendChild($link);
  });
}
